/**
 * "Enter previously submitted terms": the attested act on Gwynn's packet panel that clears a reversal's
 * REVERSAL_TERMS_UNKNOWN blocker (IF THIS THEN THAT, Process 4). Shown only while that blocker holds the packet.
 * eMerge holds no evidence of what was submitted (a Monday-origin approval), so a person who has the approved packet or
 * DCF's approval says what the terms were, with where they come from. The cover prints them as recorded by her, with
 * the date; the server refuses the entry when eMerge does hold the evidence, or when one was already entered.
 *
 * Open by default (2026-10-22): collapsed behind a button it read like a label and operators typed the terms into the case's
 * ordinary Terms section instead, which never counts for a reversal.
 * Self-contained: the panel renders it and nothing else of the panel changes.
 */
import React, { useEffect, useRef, useState } from 'react';
import { umedApi } from './umedApi';
import type { PreparationOutcome } from './umedApi';
import { Why, firstReason, useWhyId } from '../../components/emerge/WithReason';

/**
 * Where the terms come from: at least this many characters, as the server checks (reversalTermsAttestation REASON_MIN;
 * a backend test pins the two). 2026-11-03: Gwynn wrote "Mondaycrm" (9) under the old 10 and the button stayed grey,
 * unstyled, with nothing said.
 */
export const REVERSAL_REASON_MIN = 3;

type Outcome = { success: boolean; error?: string; message?: string; code?: string; status?: number };
const said = (r: Outcome): string => {
  if (r.status === undefined) return 'Could not reach the server. Nothing was recorded.';
  // The event may have committed before the failure, so this must not say "nothing was recorded".
  if (r.status >= 500) return 'The server failed. The terms may have been recorded: reload this record and check before entering them again.';
  return r.error || r.message || (r.code ? `Refused (${r.code}).` : 'The server refused this.');
};

/** The holds a save can leave behind, in words, for when the answer carries only a code. */
const HOLD_WORDS: Record<string, string> = {
  RESUBMITTAL_KIND_NOT_STATED: 'Finance has not said whether this is corrected or a full reversal',
  IDENTITY_SSN_OR_MEDICAID_MISSING: "the resident's SSN or Medicaid number is not on record",
  ATTACHMENT_FORMAT_UNSUPPORTED: 'an attached file is not a PDF, PNG or JPEG',
  LINEAGE_MISSING: 'this reversal names no original UMED',
};
const holdWords = (code: string): string => HOLD_WORDS[code] || code.replace(/_/g, ' ').toLowerCase();
const trimDot = (t: string): string => t.trim().replace(/\.+$/, '');

/** What the person is told once the terms are recorded: whether the packet is being prepared, or what else still holds it. */
export function recordedMessage(prep: PreparationOutcome | null | undefined): string {
  if (!prep) return 'Recorded. The packet has not reported its status yet: reload this record to check.';
  if (prep.outcome === 'READY' || prep.outcome === 'CURRENT') return 'Recorded. The reversal packet is being prepared with them.';
  if (prep.outcome === 'ACTION_REQUIRED') {
    const held = Array.isArray(prep.blockers) ? prep.blockers : [];
    const words = held.length ? held.map((b) => trimDot(b.message || holdWords(b.code))) : prep.code ? [holdWords(prep.code)] : [];
    return `Recorded. The packet is still held for something else: ${words.length ? words.join('; ') : 'see the holds above'}.`;
  }
  if (prep.outcome === 'NOT_PREPARED') return `Recorded, but the packet could not be prepared yet (${prep.code || 'no reason given'}).`;
  return `Recorded. The packet answered ${prep.code || prep.outcome || 'with no status'}: reload this record to check.`;
}

export const REVERSAL_TERMS_EFFECT = 'This will print on the reversal cover sheet that is faxed to DCF: "Reversal of previously submitted terms: <these terms> (as recorded by you, today\'s date; submitted <the date you give>)", with the total labelled TOTAL REVERSED. It is recorded under your name with your reason, and it cannot be changed here afterwards.';

/** Said above the form when the cover already uses the terms recorded in eMerge (`recorded`), and when it has none. */
export const RECORDED_TERMS_INTRO = 'The cover uses the terms recorded in eMerge. If different terms were submitted to DCF, type them here: they print in place of the recorded ones.';
export const NO_TERMS_INTRO = 'eMerge found no terms on record for this case. Type the terms that were submitted to DCF here.';

export default function ReversalTermsForm({ caseId, onChanged, recorded = false }: { caseId: string; onChanged: (msg: string) => void | Promise<void>; recorded?: boolean }) {
  const box = useRef<HTMLDivElement | null>(null);
  // The panel sits in a scrolling column: bring the open form into view rather than leave it clipped below the fold.
  useEffect(() => { if (box.current && typeof box.current.scrollIntoView === 'function') box.current.scrollIntoView({ block: 'nearest' }); }, []);
  const [terms, setTerms] = useState('');
  const [total, setTotal] = useState('');
  const [submittedOn, setSubmittedOn] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const whyId = useWhyId('why-reversal-terms');
  // The first thing still missing, in words beside the button (no silent grey buttons, #573).
  const missing = firstReason(
    [terms.trim().length < 3, 'Enter the terms as submitted (at least 3 characters).'],
    [total.trim() === '', 'Enter the total submitted.'],
    [!/^\d{4}-\d{2}-\d{2}$/.test(submittedOn), 'Enter the date the terms were submitted.'],
    [reason.trim().length < REVERSAL_REASON_MIN, `Say where the terms come from (at least ${REVERSAL_REASON_MIN} characters).`],
    [!confirm, 'Tick the box to confirm these are the terms that were submitted.'],
  );
  const ready = missing === null;
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await umedApi.recordReversalTerms(caseId, { terms: terms.trim(), total: total.trim(), submitted_on: submittedOn, reason: reason.trim() });
      if (!r.success) { setErr(said(r)); return; }
      setConfirm(false);
      await onChanged(recordedMessage(r.data?.preparation));
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  return (
    <div className="um-form" aria-label="Previously submitted terms" ref={box}>
      <strong>Enter previously submitted terms</strong>
      <p className="um-quiet">{recorded ? RECORDED_TERMS_INTRO : NO_TERMS_INTRO}</p>
      <label><span>Terms as submitted</span><input value={terms} onChange={(e) => setTerms(e.target.value)} maxLength={200} aria-label="Previously submitted terms text" placeholder="6 months at $966.17 = $5,797.00" /></label>
      <label><span>Total submitted ($)</span><input value={total} onChange={(e) => setTotal(e.target.value)} inputMode="decimal" aria-label="Previously submitted total" /></label>
      <label><span>Date submitted</span><input type="date" value={submittedOn} onChange={(e) => setSubmittedOn(e.target.value)} aria-label="Date the terms were submitted" /></label>
      <label><span>Where the terms come from</span><textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2} aria-label="Reason for entering the terms" /></label>
      <p className="um-quiet">{REVERSAL_TERMS_EFFECT}</p>
      <label><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} aria-label="Confirm the terms are as submitted" /> I have the approved packet or DCF&rsquo;s approval and these are the terms that were submitted.</label>
      {err && <p role="alert">{err}</p>}
      <p>
        <button type="button" className="um-open um-primary" disabled={!ready || busy} aria-describedby={!ready && !busy ? whyId : undefined} onClick={save}>{busy ? 'Recording…' : 'Record the terms'}</button>
        {' '}<Why id={whyId} reason={!busy && missing} className="um-text um-why" />
      </p>
    </div>
  );
}
